/**
 * 정지 요청 키 — "재시도해도 같은 결과".
 *
 * 관용구는 공유 일정의 `getStableSharedScheduleOperation`(features/sharedMembership/scheduleEntitlements.ts)과 같다:
 * **의도 문자열이 같으면 같은 키**, 하나라도 바뀌면 새 키.
 *
 * - 의도에는 서버 결과를 정하는 값만 넣는다(동작·수강권·정지·날짜·expectedRevision). 화면 상태는 넣지 않는다.
 * - 같은 키로 다시 보내면 서버가 처음 기록을 돌려준다(`replayed: true`, 4-2 ②). 응답 유실·타임아웃 재시도가 안전하다.
 * - 날짜나 revision 이 바뀐 요청에 옛 키를 쓰면 서버는 409 `REQUEST_KEY_REUSED` 또는 옛 결과를 돌려준다 → 반드시 새 키.
 * - 서버는 `auto:`·`handover:` 접두어와 8자 미만·128자 초과를 거절한다.
 */
import { PausePassRef } from './types';

export type PauseAction = 'open' | 'resume' | 'cancel' | 'changeEnd';

export type StablePauseRequestKey = {
    intentKey: string;
    requestKey: string;
};

export const PAUSE_REQUEST_KEY_PREFIX = 'admin-web:pause:';

const randomPart = () => {
    const randomUUID = globalThis.crypto?.randomUUID?.bind(globalThis.crypto);
    if (randomUUID) return randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const makePauseRequestKey = () => `${PAUSE_REQUEST_KEY_PREFIX}${randomPart()}`;

export const getPassRefId = (ref: PausePassRef) => (ref.kind === 'pt' ? ref.membershipId : ref.memberTicketId);

export type PauseIntent = {
    action: PauseAction;
    ref: PausePassRef;
    pauseId?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    expectedRevision?: number | null;
};

/** 의도 문자열. 순서가 고정된 배열이라 키 순서에 흔들리지 않는다. */
export const buildPauseIntentKey = ({ action, ref, pauseId, startDate, endDate, expectedRevision }: PauseIntent) =>
    JSON.stringify([
        action,
        ref.kind,
        ref.kind === 'group' ? ref.centerId : '',
        getPassRefId(ref),
        pauseId ?? '',
        // 끝나는 날 없음(null)과 미입력('')을 구분한다 — 무기한과 날짜는 다른 요청이다.
        startDate === undefined ? '' : startDate,
        endDate === undefined ? '' : endDate === null ? '<none>' : endDate,
        expectedRevision ?? '',
    ]);

/** 같은 의도면 지금 키를 그대로, 다르면 새 키를 준다. */
export const getStablePauseRequestKey = (
    current: StablePauseRequestKey | null | undefined,
    intentKey: string,
): StablePauseRequestKey => {
    if (current && current.intentKey === intentKey) return current;
    return { intentKey, requestKey: makePauseRequestKey() };
};
